import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

import { DateRecurrence, RecurrenceUnit } from '../../../objects/critical-dates';
import {
  RECURRENCE_PRESETS,
  anchorTo,
  defaultRecurrence,
  describeRecurrence,
  recurrenceLabel
} from '../../utils/date-recurrence';

/**
 * "Repeats …" control for an Important Date, modelled on the Scheduled Reports
 * schedule dialog (toggle → frequency → plain-English summary) because that is
 * the pattern Ray asked us to reuse and the one users already know.
 *
 * It is a control, not a dialog: the Important Date form is itself a dialog, and
 * a dialog opening a dialog to set one field is worse than an inline section.
 *
 * Two-way bound: `[(recurrence)]`. `anchorDate` is the date the series counts
 * from (the Important Date itself) and feeds the summary's "next one falls on";
 * without it the control still works, it just cannot preview a date.
 */
@Component({
  selector: 'app-date-recurrence-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatIconModule,
    MatButtonModule
  ],
  template: `
    <div class="recurrence-editor">
      <div class="toggle-row">
        <mat-slide-toggle
          color="primary"
          [ngModel]="isEnabled"
          (ngModelChange)="setEnabled($event)"
          [disabled]="disabled"
          name="recurrenceEnabled">
          {{ isEnabled ? 'This date repeats' : 'Repeat this date' }}
        </mat-slide-toggle>
      </div>

      @if (isEnabled) {
        <div class="fields-row">
          <mat-form-field class="frequency-field">
            <mat-label>Frequency</mat-label>
            <mat-select [ngModel]="presetKey" (ngModelChange)="setPreset($event)" name="recurrenceFrequency">
              @for (p of presets; track p.label) {
                <mat-option [value]="keyOf(p.unit, p.interval)">{{ p.label }}</mat-option>
              }
              <!-- A stored value that matches no preset (backend-written, or from a
                   future UI) still shows its real label instead of a blank select. -->
              @if (isCustomInterval) {
                <mat-option [value]="presetKey">{{ customLabel }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field class="ends-field">
            <mat-label>Ends on (optional)</mat-label>
            <input matInput
                   [matDatepicker]="endsPicker"
                   [value]="endsOnAsDate"
                   (dateChange)="setEndsOn($event)"
                   name="recurrenceEndsOn">
            <mat-datepicker-toggle matSuffix [for]="endsPicker"></mat-datepicker-toggle>
            <mat-datepicker #endsPicker></mat-datepicker>
            <mat-hint>Leave empty to repeat indefinitely</mat-hint>
          </mat-form-field>

          @if (endsOnAsDate) {
            <button mat-icon-button type="button" class="clear-ends"
                    (click)="clearEndsOn()" aria-label="Clear end date">
              <mat-icon>close</mat-icon>
            </button>
          }
        </div>

        <div class="recurrence-summary">
          <mat-icon>event_repeat</mat-icon>
          <span>{{ summary }}</span>
        </div>
      }
    </div>
  `,
  styles: [`
    .recurrence-editor {
      border: 1px solid #e0e0e0;
      border-radius: 6px;
      padding: 14px 16px;
      background: #fafafa;
    }

    .toggle-row {
      margin-bottom: 4px;
    }

    .fields-row {
      display: flex;
      align-items: flex-start;
      gap: 16px;
      flex-wrap: wrap;
      margin-top: 12px;
    }

    .frequency-field {
      flex: 1 1 200px;
      min-width: 180px;
    }

    .ends-field {
      flex: 1 1 200px;
      min-width: 180px;
    }

    .clear-ends {
      margin-top: 8px;
    }

    .recurrence-summary {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      color: #37474f;
      background: #e8f0fe;
      border-radius: 4px;
      padding: 8px 12px;
    }

    .recurrence-summary mat-icon {
      color: #1976d2;
      font-size: 18px;
      height: 18px;
      width: 18px;
    }
  `]
})
export class DateRecurrenceEditorComponent {
  /** Current value. Absent or disabled = a one-shot date. */
  @Input() recurrence: DateRecurrence | null | undefined;
  @Output() recurrenceChange = new EventEmitter<DateRecurrence | null>();

  /** The date the series counts from — the Important Date. Drives the preview only. */
  @Input() anchorDate: string | null | undefined;

  /** Locks the toggle. The host decides when repeating is meaningful. */
  @Input() disabled = false;

  readonly presets = RECURRENCE_PRESETS;

  get isEnabled(): boolean {
    return this.recurrence?.enabled === true;
  }

  get presetKey(): string {
    const rec = this.recurrence;
    return rec ? this.keyOf(rec.unit, rec.interval) : '';
  }

  get isCustomInterval(): boolean {
    const rec = this.recurrence;
    if (!rec) return false;
    return !this.presets.some(p => p.unit === rec.unit && p.interval === rec.interval);
  }

  get customLabel(): string {
    return recurrenceLabel(this.recurrence);
  }

  get endsOnAsDate(): Date | null {
    const raw = this.recurrence?.endsOn;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }

  get summary(): string {
    return describeRecurrence(this.recurrence, this.anchorDate);
  }

  keyOf(unit: RecurrenceUnit, interval: number): string {
    return `${unit}:${interval}`;
  }

  /**
   * Turning repeat off keeps the rest of the configuration rather than dropping
   * it, so a user who toggles by accident does not silently lose their
   * frequency and end date. `enabled:false` is what every reader checks.
   */
  setEnabled(enabled: boolean): void {
    if (enabled) {
      this.emit(this.reanchored({ ...(this.recurrence ?? defaultRecurrence()), enabled: true }));
    } else if (this.recurrence) {
      this.emit({ ...this.recurrence, enabled: false });
    } else {
      this.emit(null);
    }
  }

  setPreset(key: string): void {
    const [unit, rawInterval] = String(key).split(':');
    const interval = parseInt(rawInterval, 10);
    if (!unit || isNaN(interval)) return;
    this.emit(this.reanchored({
      ...(this.recurrence ?? defaultRecurrence()),
      enabled: true,
      unit: unit as RecurrenceUnit,
      interval
    }));
  }

  setEndsOn(event: any): void {
    const date: Date | null = event?.value ?? null;
    this.emit(this.reanchored({
      ...(this.recurrence ?? defaultRecurrence()),
      enabled: true,
      endsOn: date ? date.toISOString() : null
    }));
  }

  clearEndsOn(): void {
    if (!this.recurrence) return;
    this.emit({ ...this.recurrence, endsOn: null });
  }

  /**
   * Point the series at the date the user is looking at, restarting the count.
   *
   * Every edit re-anchors, which sounds aggressive but is the only rule that
   * stays honest: the anchor means "the occurrence on screen", so anchor + 0
   * steps is always the current Important Date and the next occurrence is one
   * interval on. Carrying a stale anchor plus a backend-written count through a
   * frequency change would compute occurrences for the OLD cadence.
   */
  private reanchored(rec: DateRecurrence): DateRecurrence {
    return anchorTo(rec, this.anchorDate);
  }

  private emit(value: DateRecurrence | null): void {
    this.recurrence = value;
    this.recurrenceChange.emit(value);
  }
}
